import React from "react";
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Divider
} from "@mui/material";
import { intl } from "dmsbasereact";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { APP_COLORS } from "../../theme/appTheme";

const severityConfig = {
    warning: {
        icon: WarningAmberIcon,
        color: APP_COLORS.system.warning,
        bgColor: '#FFF4E5'
    },
    error: {
        icon: ErrorOutlineIcon,
        color: APP_COLORS.system.error,
        bgColor: '#FFE5E5'
    },
    info: {
        icon: InfoOutlinedIcon,
        color: APP_COLORS.system.info,
        bgColor: APP_COLORS.brand?.bluePale || '#E3F2FD'
    }
};

export const DashboardAlertCard = ({
    title,
    items,
    severity = "warning",
    emptyMessage,
    onItemClick,
    maxItems = 5
}) => {
    const config = severityConfig[severity] || severityConfig.warning;
    const Icon = config.icon;
    const displayItems = items?.slice(0, maxItems) || [];
    const hasMore = items?.length > maxItems;

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                overflow: 'hidden'
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    backgroundColor: config.bgColor,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Icon sx={{ color: config.color, fontSize: 22 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {title}
                    </Typography>
                </Box>
                {items?.length > 0 && (
                    <Chip
                        label={items.length}
                        size="small"
                        sx={{
                            backgroundColor: config.color,
                            color: 'white',
                            fontWeight: 600,
                            height: 24,
                            minWidth: 24
                        }}
                    />
                )}
            </Box>

            {displayItems.length > 0 ? (
                <List disablePadding>
                    {displayItems.map((item, index) => (
                        <React.Fragment key={item.id || index}>
                            {index > 0 && <Divider />}
                            <ListItem
                                onClick={() => onItemClick && onItemClick(item)}
                                sx={{
                                    px: 2.5,
                                    py: 1.5,
                                    cursor: onItemClick ? 'pointer' : 'default',
                                    transition: 'background-color 0.2s',
                                    '&:hover': onItemClick ? {
                                        backgroundColor: 'action.hover'
                                    } : {}
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {item.title}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {item.description}
                                        </Typography>
                                    }
                                />
                                {onItemClick && (
                                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                                        <ChevronRightIcon color="action" />
                                    </ListItemIcon>
                                )}
                            </ListItem>
                        </React.Fragment>
                    ))}
                    {hasMore && (
                        <>
                            <Divider />
                            <ListItem sx={{ px: 2.5, py: 1.5, justifyContent: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {intl.get("AndMoreItems", { count: items.length - maxItems }).d("+ more items")}
                                </Typography>
                            </ListItem>
                        </>
                    )}
                </List>
            ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {emptyMessage || intl.get("NoAlertsFound").d("No alerts found")}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

DashboardAlertCard.propTypes = {
    title: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string.isRequired,
        description: PropTypes.string
    })),
    severity: PropTypes.oneOf(['warning', 'error', 'info']),
    emptyMessage: PropTypes.string,
    onItemClick: PropTypes.func,
    maxItems: PropTypes.number
};
